import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import './FloatingAIButton.css';

const BUTTON_SIZE_DESKTOP = 56;
const BUTTON_SIZE_MOBILE = 50;

const EDGE_MARGIN = 10;
const DEFAULT_RIGHT_OFFSET = 20;
const DEFAULT_BOTTOM_OFFSET = 120;

interface Position {
  x: number;
  y: number;
}

const FloatingAIButton: React.FC = () => {
  const navigate = useNavigate();

  const getButtonSize = () => {
    return window.innerWidth <= 768
      ? BUTTON_SIZE_MOBILE
      : BUTTON_SIZE_DESKTOP;
  };

  const getDefaultPosition = (): Position => {
    const buttonSize = getButtonSize();

    return {
      x: Math.max(
        EDGE_MARGIN,
        window.innerWidth - buttonSize - DEFAULT_RIGHT_OFFSET
      ),
      y: Math.max(
        EDGE_MARGIN,
        window.innerHeight - buttonSize - DEFAULT_BOTTOM_OFFSET
      )
    };
  };

  const [isDragging, setIsDragging] = useState(false);

  const [position, setPosition] = useState<Position>(
    getDefaultPosition()
  );

  const dragStart = useRef({ x: 0, y: 0 });
  const buttonRef = useRef<HTMLButtonElement>(null);

  /*
   * Track the viewport size so we can reposition the button
   * when the screen changes.
   */
  const previousViewport = useRef({
    width: window.innerWidth,
    height: window.innerHeight
  });

  /*
   * Track whether the user has manually positioned the button.
   */
  const hasUserPosition = useRef(false);

  /*
   * Clamp the button so it always stays inside the viewport.
   */
  const clampPosition = useCallback(
    (x: number, y: number): Position => {
      const buttonSize = getButtonSize();

      const maxX = Math.max(
        EDGE_MARGIN,
        window.innerWidth - buttonSize - EDGE_MARGIN
      );

      const maxY = Math.max(
        EDGE_MARGIN,
        window.innerHeight - buttonSize - EDGE_MARGIN
      );

      return {
        x: Math.max(EDGE_MARGIN, Math.min(maxX, x)),
        y: Math.max(EDGE_MARGIN, Math.min(maxY, y))
      };
    },
    []
  );

  /*
   * Load saved position once.
   */
  useEffect(() => {
    try {
      const saved = localStorage.getItem(
        'floatingButtonPosition'
      );

      if (!saved) {
        return;
      }

      const parsed = JSON.parse(saved);

      if (
        typeof parsed?.x !== 'number' ||
        typeof parsed?.y !== 'number'
      ) {
        return;
      }

      const clampedPosition = clampPosition(
        parsed.x,
        parsed.y
      );

      setPosition(clampedPosition);
      hasUserPosition.current = true;
    } catch {
      /*
       * Ignore invalid saved position data.
       */
      localStorage.removeItem('floatingButtonPosition');
    }
  }, [clampPosition]);

  /*
   * Save position after the user moves the button.
   */
  useEffect(() => {
    if (!isDragging && hasUserPosition.current) {
      localStorage.setItem(
        'floatingButtonPosition',
        JSON.stringify(position)
      );
    }
  }, [position, isDragging]);

  /*
   * Reposition the button whenever the viewport changes.
   *
   * If the button was near the right edge before resizing,
   * preserve its distance from the right edge.
   *
   * If it was near the bottom edge, preserve its distance
   * from the bottom edge.
   */
  useEffect(() => {
    const handleResize = () => {
      if (isDragging) {
        return;
      }

      const oldWidth = previousViewport.current.width;
      const oldHeight = previousViewport.current.height;

      const newWidth = window.innerWidth;
      const newHeight = window.innerHeight;

      const buttonSize = getButtonSize();

      const oldRightOffset =
        oldWidth - position.x - buttonSize;

      const oldBottomOffset =
        oldHeight - position.y - buttonSize;

      const wasNearRightEdge =
        oldRightOffset <= 100;

      const wasNearBottomEdge =
        oldBottomOffset <= 150;

      let newX = position.x;
      let newY = position.y;

      /*
       * Preserve right-edge positioning.
       */
      if (wasNearRightEdge) {
        newX =
          newWidth -
          buttonSize -
          Math.max(EDGE_MARGIN, oldRightOffset);
      } else {
        /*
         * Otherwise maintain proportional horizontal position.
         */
        const widthRatio =
          oldWidth > 0
            ? position.x / oldWidth
            : 0.5;

        newX = newWidth * widthRatio;
      }

      /*
       * Preserve bottom-edge positioning.
       */
      if (wasNearBottomEdge) {
        newY =
          newHeight -
          buttonSize -
          Math.max(EDGE_MARGIN, oldBottomOffset);
      } else {
        /*
         * Otherwise maintain proportional vertical position.
         */
        const heightRatio =
          oldHeight > 0
            ? position.y / oldHeight
            : 0.5;

        newY = newHeight * heightRatio;
      }

      const newPosition = clampPosition(newX, newY);

      setPosition(newPosition);

      previousViewport.current = {
        width: newWidth,
        height: newHeight
      };
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener(
        'resize',
        handleResize
      );
    };
  }, [position, isDragging, clampPosition]);

  /*
   * Handle mouse dragging.
   */
  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!isDragging) {
        return;
      }

      e.preventDefault();

      const newPosition = clampPosition(
        e.clientX - dragStart.current.x,
        e.clientY - dragStart.current.y
      );

      setPosition(newPosition);
    },
    [isDragging, clampPosition]
  );

  /*
   * Handle touch dragging.
   */
  const handleTouchMove = useCallback(
    (e: TouchEvent) => {
      if (!isDragging) {
        return;
      }

      e.preventDefault();

      const touch = e.touches[0];

      if (!touch) {
        return;
      }

      const newPosition = clampPosition(
        touch.clientX - dragStart.current.x,
        touch.clientY - dragStart.current.y
      );

      setPosition(newPosition);
    },
    [isDragging, clampPosition]
  );

  /*
   * End dragging.
   */
  const handleDragEnd = useCallback(() => {
    setIsDragging(false);
  }, []);

  /*
   * Mouse drag start.
   */
  const handleMouseDown = (
    e: React.MouseEvent
  ) => {
    e.preventDefault();

    hasUserPosition.current = true;

    setIsDragging(true);

    dragStart.current = {
      x: e.clientX - position.x,
      y: e.clientY - position.y
    };
  };

  /*
   * Touch drag start.
   */
  const handleTouchStart = (
    e: React.TouchEvent
  ) => {
    const touch = e.touches[0];

    if (!touch) {
      return;
    }

    hasUserPosition.current = true;

    setIsDragging(true);

    dragStart.current = {
      x: touch.clientX - position.x,
      y: touch.clientY - position.y
    };
  };

  /*
   * Register drag listeners.
   */
  useEffect(() => {
    if (!isDragging) {
      return;
    }

    window.addEventListener(
      'mousemove',
      handleMouseMove
    );

    window.addEventListener(
      'mouseup',
      handleDragEnd
    );

    window.addEventListener(
      'touchmove',
      handleTouchMove,
      { passive: false }
    );

    window.addEventListener(
      'touchend',
      handleDragEnd
    );

    return () => {
      window.removeEventListener(
        'mousemove',
        handleMouseMove
      );

      window.removeEventListener(
        'mouseup',
        handleDragEnd
      );

      window.removeEventListener(
        'touchmove',
        handleTouchMove
      );

      window.removeEventListener(
        'touchend',
        handleDragEnd
      );
    };
  }, [
    isDragging,
    handleMouseMove,
    handleTouchMove,
    handleDragEnd
  ]);

  /*
   * Navigate to Amaya.
   */
  const handleClick = (
    e: React.MouseEvent
  ) => {
    if (!isDragging) {
      navigate('/ai-chat');
    }
  };

  return (
    <button
      ref={buttonRef}
      className={`floating-ai-button ${
        isDragging ? 'dragging' : ''
      }`}
      onMouseDown={handleMouseDown}
      onTouchStart={handleTouchStart}
      onClick={handleClick}
      style={{
        left: `${position.x}px`,
        top: `${position.y}px`
      }}
      aria-label="Ask Amaya"
    >
      <div className="ai-pulse-ring"></div>

      <div className="ai-button-content">
        <span className="ai-icon">
          <img
            src="/185_filtered_1.png"
            alt="Amaya"
          />
        </span>
      </div>
    </button>
  );
};

export default FloatingAIButton;